import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/core/context'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
export { getContext }

export interface Tier {
  id: string
  name: string
  objectState?: string
  url?: string
}

export interface MoneyAmount {
  amount: number
  currency: string
}

export interface FieldNameEntry {
  name?: string
  abbreviation?: string
}

export interface Network {
  id: string
  tagId: string
  name: string
  objectState?: string
  createTime?: string
  updateTime?: string
  /** Problems the server found on the last save; only set on a full fetch. */
  validationWarnings?: ValidationWarning[]
  defaultCurrency: string
  availableCurrencies: string[]
  exchangeRateProvider?: string
  currencyConversionMode?: string
  capabilities: string[]
  logoUrl?: string
  reportLogoUrl?: string
  tiers: Tier[]
  defaultTier?: Tier
  timeZone: string
  requiresUserConsent?: boolean
  viewabilityMethod?: string
  eCpmMethod?: string
  advancedLogging?: boolean
  rawLogTypes?: string[]
  advertisingPlatform: boolean
  dataPlatform: boolean
  suiPlatform: boolean
  hybridMarketplacePlatform?: boolean
  marketplacePlatform: boolean
  politicalApp: boolean
  defaultCpmCostModel?: string
  paymentProvider?: string
  selfServiceAddress?: string
  openRtbBiddingFee?: number
  hideAppSections?: boolean
  dmpConfiguration?: string
  dmpEventTypes?: string[]
  useFingerprintToGenerateUserId?: boolean
  minimumCampaignFee?: MoneyAmount
  audienceFees?: Record<string, unknown>
  transcodeConfig?: { enabled: boolean; outputs: Record<string, unknown> }
  // Notifications
  notificationsUrl?: string
  // Line item defaults
  defaultLineItemState?: string
  defaultMarketplaceLineItemState?: string
  defaultLineItemDateSpan?: number
  defaultCpm?: MoneyAmount
  hideCpmFees?: boolean
  hideLineItemExternalRef?: boolean
  calendarStartDay?: string
  defaultLineItemSmoothing?: string
  defaultMarketplaceLineItemSmoothing?: string
  defaultMarketplaceTargetingOption?: string
  defaultCreativeDelivery?: string
  defaultRateLimitScope?: string
  rateLimitAlias?: string
  hideIabCategory?: boolean
  defaultCreativeName?: string
  allowReachAnalysis?: boolean
  allowAdvertiserReports?: boolean
  // Geospatial
  namedLocationCountry?: string
  geospatialTargetCentreLat?: number
  geospatialTargetCentreLon?: number
  geospatialTargetZoom?: number
  // Field name overrides
  fieldNames?: Record<string, FieldNameEntry>
  url?: string
}

function mapNetworkMinimal(n: Record<string, unknown>): Network {
  return {
    id: n.id as string,
    tagId: (n.tagId as string) ?? '',
    name: (n.name as string) ?? '',
    defaultCurrency: (n.defaultCurrency as string) ?? 'USD',
    availableCurrencies: (n.availableCurrencies as string[]) ?? [],
    capabilities: (n.capabilities as string[]) ?? [],
    tiers: (n.tiers as Tier[]) ?? [],
    timeZone: (n.timeZone as string) ?? 'UTC',
    advertisingPlatform: (n.advertisingPlatform as boolean) ?? false,
    dataPlatform: (n.dataPlatform as boolean) ?? false,
    suiPlatform: (n.suiPlatform as boolean) ?? false,
    marketplacePlatform: (n.marketplacePlatform as boolean) ?? false,
    politicalApp: (n.politicalApp as boolean) ?? false,
  }
}

function mapNetworkFull(n: Record<string, unknown>): Network {
  return {
    ...mapNetworkMinimal(n),
    objectState: n.objectState as string | undefined,
    createTime: n.createTime as string | undefined,
    updateTime: n.updateTime as string | undefined,
    validationWarnings: mapValidationWarnings(n.validationWarnings),
    exchangeRateProvider: n.exchangeRateProvider as string | undefined,
    currencyConversionMode: n.currencyConversionMode as string | undefined,
    logoUrl: n.logoUrl as string | undefined,
    reportLogoUrl: n.reportLogoUrl as string | undefined,
    defaultTier: n.defaultTier as Tier | undefined,
    requiresUserConsent: n.requiresUserConsent as boolean | undefined,
    viewabilityMethod: n.viewabilityMethod as string | undefined,
    eCpmMethod: n.eCpmMethod as string | undefined,
    advancedLogging: n.advancedLogging as boolean | undefined,
    rawLogTypes: n.rawLogTypes as string[] | undefined,
    hybridMarketplacePlatform: n.hybridMarketplacePlatform as boolean | undefined,
    defaultCpmCostModel: n.defaultCpmCostModel as string | undefined,
    paymentProvider: n.paymentProvider as string | undefined,
    selfServiceAddress: n.selfServiceAddress as string | undefined,
    openRtbBiddingFee: n.openRtbBiddingFee as number | undefined,
    hideAppSections: n.hideAppSections as boolean | undefined,
    dmpConfiguration: n.dmpConfiguration as string | undefined,
    dmpEventTypes: n.dmpEventTypes as string[] | undefined,
    useFingerprintToGenerateUserId: n.useFingerprintToGenerateUserId as boolean | undefined,
    minimumCampaignFee: n.minimumCampaignFee as MoneyAmount | undefined,
    audienceFees: n.audienceFees as Record<string, unknown> | undefined,
    transcodeConfig: n.transcodeConfig as { enabled: boolean; outputs: Record<string, unknown> } | undefined,
    notificationsUrl: n.notificationsUrl as string | undefined,
    defaultLineItemState: n.defaultLineItemState as string | undefined,
    defaultMarketplaceLineItemState: n.defaultMarketplaceLineItemState as string | undefined,
    defaultLineItemDateSpan: n.defaultLineItemDateSpan as number | undefined,
    defaultCpm: n.defaultCpm as MoneyAmount | undefined,
    hideCpmFees: n.hideCpmFees as boolean | undefined,
    hideLineItemExternalRef: n.hideLineItemExternalRef as boolean | undefined,
    calendarStartDay: n.calendarStartDay as string | undefined,
    defaultLineItemSmoothing: n.defaultLineItemSmoothing as string | undefined,
    defaultMarketplaceLineItemSmoothing: n.defaultMarketplaceLineItemSmoothing as string | undefined,
    defaultMarketplaceTargetingOption: n.defaultMarketplaceTargetingOption as string | undefined,
    defaultCreativeDelivery: n.defaultCreativeDelivery as string | undefined,
    defaultRateLimitScope: n.defaultRateLimitScope as string | undefined,
    rateLimitAlias: n.rateLimitAlias as string | undefined,
    hideIabCategory: n.hideIabCategory as boolean | undefined,
    defaultCreativeName: n.defaultCreativeName as string | undefined,
    allowReachAnalysis: n.allowReachAnalysis as boolean | undefined,
    allowAdvertiserReports: n.allowAdvertiserReports as boolean | undefined,
    namedLocationCountry: n.namedLocationCountry as string | undefined,
    geospatialTargetCentreLat: n.geospatialTargetCentreLat as number | undefined,
    geospatialTargetCentreLon: n.geospatialTargetCentreLon as number | undefined,
    geospatialTargetZoom: n.geospatialTargetZoom as number | undefined,
    fieldNames: n.fieldNames as Record<string, FieldNameEntry> | undefined,
    url: n.url as string | undefined,
  }
}

export interface NetworkInput {
  name?: string
  defaultCurrency?: string
  defaultTier?: { id: string } | null
  /** The enabled tiers, highest priority first. */
  tiers?: { id: string }[]
  reportLogoUrl?: string
  availableCurrencies?: string[]
  timeZone?: string
  exchangeRateProvider?: string
  currencyConversionMode?: string
  requiresUserConsent?: boolean
  viewabilityMethod?: string
  eCpmMethod?: string
  advancedLogging?: boolean
  rawLogTypes?: string[]
  advertisingPlatform?: boolean
  dataPlatform?: boolean
  suiPlatform?: boolean
  hybridMarketplacePlatform?: boolean
  marketplacePlatform?: boolean
  politicalApp?: boolean
  defaultCpmCostModel?: string
  paymentProvider?: string
  openRtbBiddingFee?: number
  hideAppSections?: boolean
  dmpConfiguration?: string
  useFingerprintToGenerateUserId?: boolean
  minimumCampaignFee?: MoneyAmount
  transcodeConfig?: { enabled: boolean; outputs: Record<string, unknown> }
  notificationsUrl?: string
  defaultLineItemState?: string
  defaultMarketplaceLineItemState?: string
  defaultLineItemDateSpan?: number
  defaultCpm?: MoneyAmount
  hideCpmFees?: boolean
  hideLineItemExternalRef?: boolean
  calendarStartDay?: string
  defaultLineItemSmoothing?: string
  defaultMarketplaceLineItemSmoothing?: string
  defaultMarketplaceTargetingOption?: string
  defaultCreativeDelivery?: string
  defaultRateLimitScope?: string
  rateLimitAlias?: string
  hideIabCategory?: boolean
  defaultCreativeName?: string
  allowReachAnalysis?: boolean
  allowAdvertiserReports?: boolean
  namedLocationCountry?: string
  geospatialTargetCentreLat?: number
  geospatialTargetCentreLon?: number
  geospatialTargetZoom?: number
  fieldNames?: Record<string, FieldNameEntry>
}

export interface NetworkProfile {
  name?: string
  profile?: { adnDefaults?: string; adnUiLogo?: string }
}

export const NetworkProfilesApi = {
  get: async (tagId: string): Promise<NetworkProfile> => {
    const res = await apiFetch(`${API_BASE}/networkprofiles/${encodeURIComponent(tagId)}?context=${getContext()}`)
    if (!res.ok) await throwApiError(res, 'Failed to fetch network profile')
    return res.json()
  },

  update: async (tagId: string, payload: NetworkProfile): Promise<NetworkProfile> => {
    const res = await apiFetch(`${API_BASE}/networkprofiles/${encodeURIComponent(tagId)}?context=${getContext()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!res.ok) await throwApiError(res, 'Failed to update network profile')
    return res.json()
  },
}

export const NetworksApi = {
  update: async (id: string, input: NetworkInput): Promise<Network> => {
    const res = await apiFetch(`${API_BASE}/networks/${encodeURIComponent(id)}?context=${getContext()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to update network')
    const data = await res.json()
    return mapNetworkFull(data)
  },

  getByTagId: async (tagId: string): Promise<Network> => {
    const res = await apiFetch(`${API_BASE}/networks/${encodeURIComponent(tagId)}`, {
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch network')
    const data = await res.json()
    return mapNetworkFull(data)
  },

  getForObject: async (objectId: string): Promise<Network[]> => {
    const params = new URLSearchParams({
      context: getContext(),
      includeActive: 'true',
      includeHidden: 'false',
      includeInactive: 'false',
      object: objectId,
      orderBy: 'name',
      pageSize: '1001',
    })
    const res = await apiFetch(`${API_BASE}/networks?${params}`, {
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) throw new Error(`Failed to fetch networks (${res.status})`)
    const data = await res.json()
    return toArray(data).map(mapNetworkMinimal)
  },

  getAll: async (): Promise<Network[]> => {
    const params = new URLSearchParams({
      includeActive: 'true',
      includeHidden: 'false',
      includeInactive: 'false',
      minimal: 'true',
      orderBy: 'name',
      pageSize: '1000',
      useNetworkTags: 'true',
    })
    const res = await apiFetch(`${API_BASE}/networks?${params}`, {
      headers: { 'Content-Type': 'application/json' },
    })
    if (!res.ok) throw new Error(`Failed to fetch networks (${res.status})`)
    const data = await res.json()
    return toArray(data).map(mapNetworkMinimal)
  },
}
